import React, { useState, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ActionLockDesktopTimeline from './ActionLockDesktopTimeline';
import ActionLockMobileTimeline from './ActionLockMobileTimeline';
import {
  dialogContent,
  dialogTitle,
  dialogCloseButton,
  timelineDialogStyles,
} from './actionLockTimelineStyles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ActionLockEvent } from '../../../types/types';
import LockClockIcon from '@mui/icons-material/LockClock';

interface ActionLockTimelineProps {
  events: ActionLockEvent[];
}

const ActionLockTimeline: React.FC<ActionLockTimelineProps> = ({ events }) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contentSx = useMemo(() => dialogContent(isMobile), [isMobile]);

  const handleClickItem = (id: number) => setSelectedId((prev) => (prev === id ? null : id));
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (open) setSelectedId(events[0]?.id || null);
  }, [open]);

  return (
    <>
      <Button
        variant="contained"
        startIcon={<LockClockIcon />}
        sx={timelineDialogStyles.button}
        onClick={() => setOpen(true)}
      >
        Action Lock Timeline
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: 'hidden' } }}
      >
        <DialogTitle sx={dialogTitle(isMobile)}>
          <Box sx={timelineDialogStyles.titleBox}>
            <InfoOutlinedIcon fontSize="small" />
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={timelineDialogStyles.title}>
              Action Lock Timeline
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            aria-label="Close dialog"
            sx={dialogCloseButton(isMobile)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ display: isMobile ? 'none' : 'block' }} />
        <DialogContent dividers={!isMobile} sx={contentSx}>
          {isMobile ? (
            <ActionLockMobileTimeline
              events={events}
              selectedId={selectedId}
              onSelect={handleClickItem}
            />
          ) : (
            <ActionLockDesktopTimeline
              events={events}
              selectedId={selectedId}
              onSelect={handleClickItem}
            />
          )}
        </DialogContent>
        <DialogActions sx={timelineDialogStyles.actionButton}>
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionLockTimeline;
